import React, { useState } from 'react';
import BidModalSC from './bid-modal.styled';
import { HeadingTwo, SmallText } from '../../typography';
import InlineLoading from '../../ui/inline-loading';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import dlt from '../../../utils/default-lang';
import Button from '../../ui/button';
import numeral from 'numeral';
import { useEffect } from 'react';
import InputGroup from '../../ui/input';
import { connect } from 'react-redux';
import BalanceAlert from '../../balance-alert';
import SummaryTable from '../components/summary-table';

const BidModal = ({
  offerDetail,
  onSubmit,
  instruction,
  intl,
  type,
  balance,
  secondOffer,
  isBiddingTable,
}) => {
  const [isError, setIsError] = useState(false);
  const [bidAmount, setBidAmount] = useState('');

  const free = Number(balance?.free || 0);
  const tied = Number(balance?.tied || 0);
  const reserved = Number(offerDetail.reserved_resources || 0);

  const bidNumber = bidAmount.replace(',', '.');
  const notEnoughResources =
    bidAmount !== '' && Number((Number(bidNumber) - reserved).toFixed(2)) > free + tied;

  const nonReinvestFunds =
    bidAmount !== '' && Number((Number(bidNumber) - reserved).toFixed(2)) > free;

  const balanceError = React.useMemo(() => {
    if (notEnoughResources) {
      return (
        <BalanceAlert
          type="error"
          description={
            <FormattedMessage
              id="investor_secondary_market_bid_modal_error_resources"
              defaultMessage={dlt['investor_secondary_market_bid_modal_error_resources']}
            />
          }
        />
      );
    } else if (nonReinvestFunds) {
      return (
        <BalanceAlert
          type="warning"
          description={
            <FormattedMessage
              id="investor_secondary_market_modal_error_free_resources"
              defaultMessage={dlt['investor_secondary_market_modal_error_free_resources']}
            />
          }
        />
      );
    }

    return null;
  }, [notEnoughResources, nonReinvestFunds]);

  useEffect(() => {
    if (instruction.interestRate > Number(offerDetail.interestRate) * 100) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [instruction, offerDetail]);

  const onBidAmountChange = (e) => {
    const re = /^[\d+\s?]+,?(\d+)?$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      setBidAmount(e.target.value);
    }
  };

  if (offerDetail === null || Object.keys(offerDetail).length < 1) {
    return (
      <BidModalSC>
        <div className="modal-loading-wrap">
          <InlineLoading />
        </div>
      </BidModalSC>
    );
  }
  return (
    <BidModalSC>
      <div className="top-text-wrap">
        <HeadingTwo className="bold">
          <FormattedHTMLMessage
            id="investor_secondary_market_bid_modal_headline"
            defaultMessage={dlt['investor_secondary_market_bid_modal_headline']}
          />
        </HeadingTwo>
      </div>
      {secondOffer ? (
        <div className="second-offer">
          <div className="second-offer--item">
            <SmallText className="black-transparent">
              <FormattedMessage
                id="investor_bidding_table_head_3"
                defaultMessage={dlt['investor_bidding_table_head_3']}
              />
            </SmallText>
            <h2>{numeral(Number(offerDetail.seller_last_bid)).format('0,00.00')} €</h2>
          </div>
          <div className="second-offer--divider" />
          <div className="second-offer--item">
            <SmallText className="black-transparent">
              <FormattedMessage
                id="investor_bidding_table_head_4"
                defaultMessage={dlt['investor_bidding_table_head_4']}
              />
            </SmallText>
            <h2>{numeral(Number(offerDetail.buyer_last_bid)).format('0,00.00')} €</h2>
          </div>
        </div>
      ) : (
        <div className="price-wrap">
          <h2>{numeral(Number(offerDetail.price)).format('0,00.00')} €</h2>
          <SmallText className="bolder black-transparent-lighter">
            <FormattedMessage
              id="investor_secondary_market_buy_modal_price"
              defaultMessage={dlt['investor_secondary_market_buy_modal_price']}
            />
          </SmallText>
        </div>
      )}
      <SummaryTable offerDetail={offerDetail} isBiddingTable={isBiddingTable} />
      {isError ? (
        <BalanceAlert
          type="error"
          description={
            <FormattedHTMLMessage
              id="investor_secondary_market_buy_modal_error_rate"
              defaultMessage={dlt['investor_secondary_market_buy_modal_error_rate']}
              values={{
                loanRate: numeral(Number(offerDetail.interestRate) * 100).format('0.[00]'),
                investorRate: instruction.interestRate,
              }}
            />
          }
        />
      ) : null}
      <div className="bid-wrap">
        <InputGroup
          value={bidAmount}
          placeholder={`${numeral(Number(offerDetail.price)).format('0,00.00')} €`}
          label={intl.formatMessage({
            id:
              type === 'sell'
                ? 'investor_secondary_market_bid_modal_input_label'
                : 'investor_secondary_market_bid_modal_input_label_buy',
          })}
          onChange={(e) => onBidAmountChange(e)}
        />
      </div>
      {balanceError}
      <div className="submit-wrap">
        <Button
          type="PRIMARY"
          size="BIG"
          onClick={
            secondOffer
              ? () => onSubmit(offerDetail.offer_id, offerDetail.bidding_id, bidAmount)
              : () => onSubmit(offerDetail.id, bidAmount)
          }
          disabled={
            bidAmount === '' ||
            bidAmount === ',' ||
            bidAmount === null ||
            parseFloat(bidAmount.replace(',', '.')) === 0 ||
            notEnoughResources
          }
        >
          <FormattedMessage id="global_bid" defaultMessage={dlt['global_bid']} />
        </Button>
      </div>
    </BidModalSC>
  );
};

const mapStateToProps = (state) => ({
  balance: state.dashboard.balance,
});

export default connect(mapStateToProps)(injectIntl(BidModal));
